/**
 * DriverMarketplaceFeed — bottom marketplace pill.
 *
 * Collapsed: a compact pill showing the SINGLE best available block
 * (highest value across the next 14 days). The pill sits at the bottom
 * of the map and is always visible alongside the rest of the floating
 * chrome.
 *
 * Expanded: morphs into a slide-up drawer containing
 *   - a 14-day calendar navigator (today + 13 forward)
 *   - the full list of available blocks for the selected day
 *   - tap a card → motion-morph into the detail sheet with CLAIM button
 *
 * Powered by `/api/driver/marketplace/blocks?date=&status=available`
 * which reads V1 payment-source-of-truth attribution.
 */
import React, { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useDragDismiss } from "@/shared/motion/useDragDismiss";
import { Loader2, MapPin, Clock, X, Check, ChevronUp, ChevronDown, ChevronRight, RefreshCcw } from "lucide-react";
import { toast } from "sonner";

import { api, formatApiError } from "@/shared/lib/api";
import { DURATION, EASE, SPRING } from "@/shared/motion/tokens";

const DAYS_AHEAD = 14;
const DAY_MS = 86400000;

function isoOf(offset) {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + offset);
  return d.toISOString().slice(0, 10);
}

function formatTime12(hhmm) {
  if (!hhmm || !/^\d{1,2}:\d{2}/.test(hhmm)) return hhmm || "—";
  const [h, m] = hhmm.split(":").map(Number);
  const period = h >= 12 ? "PM" : "AM";
  const h12 = ((h + 11) % 12) + 1;
  return `${h12}:${m.toString().padStart(2, "0")} ${period}`;
}

// Returns hhmm + 15 min (e.g. "04:15" → "04:30") for the
// "Pickup 4:15 AM – 4:30 AM" 15-minute rider-booked window display.
function fifteenAfter(hhmm) {
  if (!hhmm || !/^\d{1,2}:\d{2}/.test(hhmm)) return null;
  const [h, m] = hhmm.split(":").map(Number);
  const total = (h * 60 + m + 15) % (24 * 60);
  return `${String(Math.floor(total / 60)).padStart(2, "0")}:${String(total % 60).padStart(2, "0")}`;
}


export default function DriverMarketplaceFeed({ onClaimed, sectors = [], bottomOffset = 80, activeSector = null, onCloseSector }) {
  const [expanded, setExpanded] = useState(false);
  const [size, setSize] = useState("medium"); // 'small' | 'medium' (large = expanded)
  const [allBlocks, setAllBlocks] = useState([]); // flat list across 14d
  const [selectedOffset, setSelectedOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [claiming, setClaiming] = useState(false);

  // When a sector is tapped on the map, the parent forwards it via
  // `activeSector`. We force the drawer open and filter the list to
  // only that sector's blocks. Closing the drawer also clears the
  // active sector through the parent callback.
  useEffect(() => {
    if (activeSector) {
      setExpanded(true);
    }
  }, [activeSector]);

  // Fetch 14 days in parallel — one call per day. Keeps the backend
  // simple while giving us the calendar overview.
  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const dates = Array.from({ length: DAYS_AHEAD }, (_, i) => isoOf(i));
      const responses = await Promise.all(
        dates.map((iso) => api.get(`/driver/marketplace/blocks?date=${iso}&status=available`)
          .then((r) => r.data?.blocks || [])
          .catch(() => []))
      );
      setAllBlocks(responses.flat());
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Could not load marketplace");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);
  useEffect(() => {
    const id = setInterval(loadAll, 30000);
    return () => clearInterval(id);
  }, [loadAll]);

  // When a sector becomes active, jump to its earliest day with blocks
  // so the driver immediately sees work for that area.
  useEffect(() => {
    if (!activeSector) return;
    const sectorBlocks = allBlocks.filter((b) => b.sector_id === activeSector.id);
    if (sectorBlocks.length === 0) return;
    const dates = new Set(sectorBlocks.map((b) => b.date).filter(Boolean));
    for (let i = 0; i < DAYS_AHEAD; i += 1) {
      if (dates.has(isoOf(i))) { setSelectedOffset(i); return; }
    }
  }, [activeSector, allBlocks]);

  // Unified close — collapses the drawer AND clears the active sector
  // on the parent. The drawer is the single surface; there is no
  // separate sector pill anymore.
  const closeDrawer = useCallback(() => {
    setExpanded(false);
    onCloseSector?.();
  }, [onCloseSector]);

  // When a sector is selected, only operate on that sector's blocks
  // for ALL downstream computations (best block, day counts, day list).
  const filteredBlocks = useMemo(() => {
    if (!activeSector) return allBlocks;
    return allBlocks.filter((b) => b.sector_id === activeSector.id);
  }, [allBlocks, activeSector]);

  // Best block = highest pay across the 14d window.
  const bestBlock = useMemo(() => {
    if (filteredBlocks.length === 0) return null;
    return filteredBlocks.reduce((best, b) =>
      (b.pay_dollars || 0) > (best.pay_dollars || 0) ? b : best, filteredBlocks[0]);
  }, [filteredBlocks]);

  // Per-day counts for the calendar dots.
  const dayCounts = useMemo(() => {
    const map = new Map();
    for (const b of filteredBlocks) {
      map.set(b.date, (map.get(b.date) || 0) + 1);
    }
    return map;
  }, [filteredBlocks]);

  const selectedDate = isoOf(selectedOffset);
  const selectedDayBlocks = useMemo(
    () => filteredBlocks.filter((b) => b.date === selectedDate)
      .sort((a, b) => ((a.pickup_time || a.window_start) || "").localeCompare((b.pickup_time || b.window_start) || "")),
    [filteredBlocks, selectedDate],
  );

  const claim = async () => {
    if (!selected) return;
    setClaiming(true);
    try {
      await api.post(`/driver/marketplace/blocks/${selected.id}/claim`);
      toast.success("Block claimed");
      setSelected(null);
      setExpanded(false);
      onCloseSector?.();
      setAllBlocks((prev) => prev.filter((b) => b.id !== selected.id));
      onClaimed?.();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Could not claim");
    } finally {
      setClaiming(false);
    }
  };

  // Drag-to-dismiss the expanded drawer. Bottom sheet → pull DOWN to
  // close. Scroll guard wired to the inner overflow-y-auto so drag
  // only begins when the calendar is at the top.
  const drawerScrollRef = useRef(null);
  const drawerDrag = useDragDismiss({
    onDismiss: closeDrawer,
    origin: "bottom",
    open: expanded,
    scrollRef: drawerScrollRef,
  });

  return (
    <>
      {/* Collapsed morph card — anchored bottom-center, ABOVE the
       *  MAP|ACCOUNT pill. Centered via a non-motion flex wrapper so
       *  framer-motion's layoutId doesn't override the transform. */}
      <div
        className="fixed inset-x-0 z-30 pointer-events-none flex justify-center px-3"
        style={{ bottom: `calc(env(safe-area-inset-bottom, 0px) + ${bottomOffset}px)` }}
      >
        <motion.div
          layoutId="market-pill"
          className="pointer-events-auto w-full max-w-[400px]"
          style={{ borderRadius: 28 }}
          animate={{ opacity: expanded ? 0 : 1, pointerEvents: expanded ? "none" : "auto" }}
          transition={{ duration: DURATION.fast, ease: EASE.out }}
          data-testid="marketplace-morph-card"
        >
        {loading && allBlocks.length === 0 ? (
          <div className="rounded-3xl bg-white border border-black/[0.06] shadow-[0_20px_50px_rgba(10,17,40,0.20)] p-6 text-center" data-testid="marketplace-morph-loading">
            <Loader2 size={18} className="animate-spin inline text-[#515E7B]"/>
            <div className="text-[12px] text-[#515E7B] mt-2">Loading marketplace…</div>
          </div>
        ) : size === "small" && bestBlock ? (
          /* ---- SMALL state ---- compact one-line pill showing only
           *  the top opportunity's $ + area. Tap to expand back to the
           *  medium featured card. Restore button (chevron-up) on the
           *  right; tapping the body itself also restores. */
          <div
            role="button"
            tabIndex={0}
            onClick={() => setSize("medium")}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") setSize("medium"); }}
            className="relative w-full rounded-full bg-white border border-black/[0.06] shadow-[0_20px_50px_rgba(10,17,40,0.20)] pl-5 pr-14 h-12 flex items-center link-pressable cursor-pointer"
            data-testid="marketplace-morph-small"
          >
            <div className="min-w-0 flex-1 flex items-center gap-2">
              <span className="text-[9.5px] tracking-[0.24em] uppercase font-semibold text-[#ed6500]">Top</span>
              <span className="text-[15px] font-serif text-[#0A1128]" data-testid="marketplace-morph-small-pay">
                ${(bestBlock.pay_dollars || 0).toFixed(0)}
              </span>
              <span className="text-[11.5px] text-[#515E7B] truncate" data-testid="marketplace-morph-small-area">
                · {bestBlock.area_label || "Block"}
              </span>
            </div>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setSize("medium"); }}
              aria-label="Expand marketplace"
              className="absolute top-1/2 -translate-y-1/2 right-2 w-9 h-9 rounded-full bg-[#F1ECE3] hover:bg-[#ed6500]/15 text-[#515E7B] hover:text-[#ed6500] inline-flex items-center justify-center transition-colors link-pressable"
              data-testid="marketplace-morph-restore-icon"
            >
              <ChevronUp size={14} strokeWidth={2.2} />
            </button>
          </div>
        ) : !bestBlock ? (
          /* ---- Empty state ---- compact pill matching the rider's
           *  address pill size. Small refresh icon on the right;
           *  tapping the body still expands into the 14-day calendar
           *  so the driver can confirm zero work and scroll forward. */
          <div
            role="button"
            tabIndex={0}
            onClick={() => setExpanded(true)}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") setExpanded(true); }}
            className="relative w-full rounded-full bg-white border border-black/[0.06] shadow-[0_20px_50px_rgba(10,17,40,0.20)] pl-5 pr-14 h-14 flex items-center link-pressable cursor-pointer"
            data-testid="marketplace-morph-empty"
          >
            <div className="min-w-0 flex-1">
              <div className="text-[9.5px] tracking-[0.28em] uppercase font-semibold text-[#ed6500]">No blocks available</div>
              <div className="text-[11.5px] text-[#515E7B] mt-0.5 truncate">Check back later — new blocks appear as riders book.</div>
            </div>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); loadAll(); }}
              aria-label="Refresh marketplace"
              className="absolute top-1/2 -translate-y-1/2 right-2 w-10 h-10 rounded-full bg-[#F1ECE3] hover:bg-[#ed6500]/15 text-[#515E7B] hover:text-[#ed6500] inline-flex items-center justify-center transition-colors link-pressable"
              data-testid="marketplace-morph-refresh-icon"
            >
              <RefreshCcw size={14} strokeWidth={2.2} className={loading ? "animate-spin" : ""} />
            </button>
          </div>
        ) : (
          /* ---- With-blocks state ---- "Top opportunity" featured card */
          <div
            role="button"
            tabIndex={0}
            onClick={() => setExpanded(true)}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") setExpanded(true); }}
            className="relative block w-full text-left rounded-3xl bg-white border border-black/[0.06] shadow-[0_20px_50px_rgba(10,17,40,0.20)] p-5 link-pressable cursor-pointer"
            data-testid="marketplace-morph-featured"
          >
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); loadAll(); }}
              aria-label="Refresh marketplace"
              className="absolute top-3 right-12 w-9 h-9 rounded-full bg-[#F1ECE3] hover:bg-[#ed6500]/15 text-[#515E7B] hover:text-[#ed6500] inline-flex items-center justify-center transition-colors link-pressable"
              data-testid="marketplace-morph-refresh-icon"
            >
              <RefreshCcw size={14} strokeWidth={2.2} className={loading ? "animate-spin" : ""} />
            </button>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setSize("small"); }}
              aria-label="Minimize marketplace"
              className="absolute top-3 right-3 w-9 h-9 rounded-full bg-[#F1ECE3] hover:bg-[#ed6500]/15 text-[#515E7B] hover:text-[#ed6500] inline-flex items-center justify-center transition-colors link-pressable"
              data-testid="marketplace-morph-minimize-icon"
            >
              <ChevronDown size={14} strokeWidth={2.2} />
            </button>
            <div className="flex items-start justify-between gap-2 pr-12">
              <div className="text-[9.5px] tracking-[0.32em] uppercase font-semibold text-[#ed6500]">
                Top opportunity
              </div>
            </div>
            <div className="font-serif text-[40px] leading-none text-[#0A1128] mt-1.5" data-testid="marketplace-morph-pay">
              ${(bestBlock.pay_dollars || 0).toFixed(0)}
            </div>
            <div className="text-[13.5px] text-[#0A1128] font-medium mt-2">
              {bestBlock.area_label || "Block"}
            </div>
            <div className="text-[12px] text-[#515E7B] mt-1">
              {bestBlock.stops_count || (bestBlock.legs_count || 1)} Stops
              {bestBlock.estimated_minutes ? ` · ${Math.round(bestBlock.estimated_minutes / 60 * 10) / 10} Hours` : ""}
            </div>
            {(bestBlock.pickup_time || (bestBlock.window_start && bestBlock.window_end)) && (
              <div className="text-[11px] text-[#515E7B] mt-0.5">
                {bestBlock.pickup_time
                  ? `Pickup ${formatTime12(bestBlock.pickup_time)}–${formatTime12(fifteenAfter(bestBlock.pickup_time))}${bestBlock.dropoff_time ? ` · Dropoff ${formatTime12(bestBlock.dropoff_time)}` : ""}`
                  : `${formatTime12(bestBlock.window_start)} – ${formatTime12(bestBlock.window_end)}`}
              </div>
            )}
            <span
              className="mt-4 w-full h-12 rounded-2xl bg-[#ed6500] text-white text-[12px] tracking-[0.22em] uppercase font-semibold inline-flex items-center justify-center gap-1.5"
              data-testid="marketplace-morph-claim-cta"
            >
              Claim Block
              <ChevronRight size={14} strokeWidth={2.2} />
            </span>
            {/* "+N more" indicator — communicates that the featured
                card is just one of many available blocks. Tapping the
                card expands the full marketplace view. */}
            {filteredBlocks.length > 1 && (
              <div
                className="mt-2 text-center text-[10px] tracking-[0.22em] uppercase font-semibold text-[#515E7B]"
                data-testid="marketplace-morph-more-count"
              >
                +{filteredBlocks.length - 1} more block{filteredBlocks.length - 1 === 1 ? "" : "s"} available — tap to view
              </div>
            )}
            <div className="absolute bottom-3 right-4 text-[#515E7B]">
              <ChevronUp size={14} strokeWidth={2.2} />
            </div>
          </div>
        )}
      </motion.div>
      </div>

      {/* Expanded drawer — calendar + day's blocks */}
      <AnimatePresence>
        {expanded && (
          <>
            <motion.div
              key="mp-backdrop"
              className="fixed inset-0 z-40 bg-black/45"
              style={{ opacity: drawerDrag.backdropOpacity, backdropFilter: "blur(8px)", WebkitBackdropFilter: "blur(8px)" }}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: DURATION.normal, ease: EASE.out }}
              onClick={closeDrawer}
              data-testid="marketplace-drawer-backdrop"
            />
            <motion.div
              key="mp-drawer"
              layoutId="market-pill"
              className="fixed inset-x-0 bottom-0 z-50 bg-[#F9F7F4] rounded-t-[28px] shadow-[0_-24px_60px_rgba(10,17,40,0.22)] overflow-hidden"
              style={{ maxHeight: "85vh", borderRadius: 28, ...drawerDrag.motionProps.style }}
              transition={SPRING.morph}
              role="dialog"
              aria-modal="true"
              data-testid="marketplace-drawer"
              drag={drawerDrag.motionProps.drag}
              dragConstraints={drawerDrag.motionProps.dragConstraints}
              dragElastic={drawerDrag.motionProps.dragElastic}
              dragMomentum={drawerDrag.motionProps.dragMomentum}
              onDragStart={drawerDrag.motionProps.onDragStart}
              onDrag={drawerDrag.motionProps.onDrag}
              onDragEnd={drawerDrag.motionProps.onDragEnd}
            >
              <div className="flex items-center justify-center pt-3 pb-1">
                <span className="w-10 h-1 rounded-full bg-[#0A1128]/15" />
              </div>
              <button
                type="button"
                onClick={closeDrawer}
                className="absolute top-3 right-4 w-9 h-9 rounded-full bg-white border border-black/[0.06] flex items-center justify-center link-pressable z-10"
                aria-label="Close marketplace"
                data-testid="marketplace-drawer-close"
              >
                <X size={16} strokeWidth={1.8} />
              </button>

              <motion.div
                ref={drawerScrollRef}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.06, duration: DURATION.normal, ease: EASE.out }}
                className="px-5 pt-2 pb-[max(env(safe-area-inset-bottom),24px)] overflow-y-auto"
                style={{ maxHeight: "calc(85vh - 24px)" }}
              >
                <div className="text-[10px] tracking-[0.32em] uppercase font-semibold text-[#ed6500]">
                  {activeSector ? "Sector" : "Marketplace"}
                </div>
                <h1 className="font-serif font-light text-[28px] leading-tight text-[#0A1128] mt-1">
                  {activeSector
                    ? activeSector.name
                    : (filteredBlocks.length === 0
                        ? "No work available"
                        : `${filteredBlocks.length} blocks across 14 days`)}
                </h1>
                {activeSector && (
                  <div className="text-[12px] text-[#515E7B] mt-1" data-testid="marketplace-sector-count">
                    {filteredBlocks.length} block{filteredBlocks.length === 1 ? "" : "s"} available · next 14 days
                  </div>
                )}

                {/* 14-day calendar strip */}
                <div className="mt-4 -mx-2 px-2 overflow-x-auto" data-testid="marketplace-calendar">
                  <div className="flex gap-1.5 pb-1">
                    {Array.from({ length: DAYS_AHEAD }, (_, i) => {
                      const iso = isoOf(i);
                      const d = new Date(iso + "T00:00:00");
                      const dow = d.toLocaleDateString(undefined, { weekday: "short" }).slice(0, 3);
                      const dom = d.getDate();
                      const count = dayCounts.get(iso) || 0;
                      const active = selectedOffset === i;
                      const today = i === 0;
                      return (
                        <button
                          key={iso}
                          type="button"
                          onClick={() => setSelectedOffset(i)}
                          className={`shrink-0 w-14 h-16 rounded-2xl flex flex-col items-center justify-center link-pressable transition-colors ${
                            active ? "bg-[#0A1128] text-white" :
                            count > 0 ? "bg-white border border-black/[0.06] text-[#0A1128]" :
                            "bg-[#F1ECE3] text-[#515E7B]"
                          }`}
                          data-testid={`market-day-${i}`}
                        >
                          <div className={`text-[9px] tracking-[0.18em] uppercase font-semibold ${active ? "opacity-70" : "opacity-60"}`}>{today ? "Today" : dow}</div>
                          <div className="font-serif text-[18px] leading-none mt-1">{dom}</div>
                          {count > 0 && !active && <div className="w-1 h-1 rounded-full bg-[#ed6500] mt-1.5"/>}
                          {count > 0 && active && <div className="text-[9px] mt-1 opacity-70">{count}</div>}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Selected day's blocks */}
                <div className="mt-4 space-y-2" data-testid="marketplace-day-list">
                  {selectedDayBlocks.length === 0 ? (
                    <div className="text-center py-10 text-[13px] text-[#515E7B]">
                      No available work for this day
                    </div>
                  ) : selectedDayBlocks.map((b) => (
                    <motion.button
                      key={b.id}
                      type="button"
                      layoutId={`block-card-${b.id}`}
                      onClick={() => setSelected(b)}
                      className="w-full text-left bg-white rounded-2xl border border-black/[0.06] shadow-sm p-3.5 flex items-center gap-3 link-pressable"
                      whileTap={{ scale: 0.98 }}
                      transition={{ duration: DURATION.fast, ease: EASE.out }}
                      data-testid={`block-card-${b.id}`}
                    >
                      <div className="shrink-0 w-12 h-12 rounded-2xl bg-[#0A1128] text-white flex flex-col items-center justify-center">
                        <div className="font-serif text-[14px] leading-none">{formatTime12(b.pickup_time || b.window_start).split(" ")[0]}</div>
                        <div className="text-[8px] tracking-[0.18em] uppercase opacity-70 mt-0.5">{formatTime12(b.pickup_time || b.window_start).split(" ")[1]}</div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-baseline gap-2">
                          <div className="font-serif text-[20px] text-[#0A1128] leading-none">${(b.pay_dollars || 0).toFixed(2)}</div>
                          <div className="text-[10px] text-[#515E7B]">{b.legs_count || 1} {b.legs_count === 1 ? "leg" : "legs"}</div>
                        </div>
                        <div className="text-[10px] text-[#515E7B] mt-1 flex items-center gap-1.5">
                          <Clock size={10}/> {b.estimated_minutes || 0} min
                          {b.area_label && <><span>·</span><MapPin size={10}/>{b.area_label}</>}
                        </div>
                      </div>
                      <div className="text-[9px] tracking-[0.18em] uppercase font-semibold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200">Available</div>
                    </motion.button>
                  ))}
                </div>
              </motion.div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Block detail morph */}
      <AnimatePresence>
        {selected && (
          <>
            <motion.div
              key="bd-backdrop"
              className="fixed inset-0 z-[55] bg-black/45"
              initial={{ opacity: 0, backdropFilter: "blur(0px)" }}
              animate={{ opacity: 1, backdropFilter: "blur(8px)" }}
              exit={{ opacity: 0, backdropFilter: "blur(0px)" }}
              transition={{ duration: DURATION.normal, ease: EASE.out }}
              onClick={() => setSelected(null)}
            />
            <motion.div
              key="bd-card"
              layoutId={`block-card-${selected.id}`}
              className="fixed inset-x-0 bottom-0 z-[60] bg-white rounded-t-[28px] shadow-[0_-24px_60px_rgba(10,17,40,0.22)] overflow-hidden"
              style={{ maxHeight: "88vh", borderRadius: 28 }}
              transition={SPRING.morph}
              role="dialog"
              aria-modal="true"
              data-testid="block-detail-sheet"
            >
              <BlockDetail block={selected} claiming={claiming} onClose={() => setSelected(null)} onClaim={claim} />
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}

function BlockDetail({ block, claiming, onClose, onClaim }) {
  const dollars = (block.pay_dollars || 0).toFixed(2);
  // Show the 15-minute rider-booked pickup window; fall back to the
  // 4-hour bucket only for legacy blocks that pre-date pickup_time.
  const pickupWindow = block.pickup_time
    ? `${formatTime12(block.pickup_time)} – ${formatTime12(fifteenAfter(block.pickup_time))}`
    : (block.window_start && block.window_end
        ? `${formatTime12(block.window_start)} – ${formatTime12(block.window_end)}`
        : "—");
  const dropoff = block.dropoff_time ? formatTime12(block.dropoff_time) : "—";
  const date = block.date ? new Date(block.date + "T00:00:00").toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" }) : "—";
  return (
    <>
      <div className="flex items-center justify-center pt-3 pb-1">
        <span className="w-10 h-1 rounded-full bg-[#0A1128]/15" />
      </div>
      <button
        type="button"
        onClick={onClose}
        className="absolute top-3 right-4 w-9 h-9 rounded-full bg-[#F9F7F4] border border-black/[0.06] flex items-center justify-center link-pressable"
        aria-label="Close"
        data-testid="block-detail-close"
      >
        <X size={16}/>
      </button>
      <motion.div
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.06, duration: DURATION.normal, ease: EASE.out }}
        className="px-5 pb-6 pt-2 overflow-y-auto"
        style={{ maxHeight: "calc(88vh - 24px)" }}
      >
        <div className="text-[10px] tracking-[0.32em] uppercase font-semibold text-[#ed6500]">Block detail</div>
        <h1 className="font-serif font-light text-[44px] leading-[1.05] text-[#0A1128] mt-1">${dollars}</h1>
        <div className="text-[11px] tracking-[0.22em] uppercase font-semibold text-[#515E7B] mt-1">
          {block.legs_count || 1} {block.legs_count === 1 ? "leg" : "legs"} · {block.estimated_minutes || 0} min est.
        </div>

        <div className="grid grid-cols-2 gap-2 mt-5">
          <Tile label="Date" v={date}/>
          <Tile label="Pickup window" v={pickupWindow}/>
          <Tile label="Dropoff (est.)" v={dropoff}/>
          <Tile label="Sector" v={block.area_label || "—"}/>
          <Tile label="Stops" v={`${block.pickup_count || 0} pickups · ${block.dropoff_count || 0} dropoffs`}/>
          <Tile label="Duration (est.)" v={`${block.estimated_minutes || 0} min`}/>
        </div>

        {(block.legs || []).length > 0 && (
          <div className="mt-5">
            <div className="text-[10px] tracking-[0.28em] uppercase font-semibold text-[#515E7B] mb-2">Payment attribution</div>
            <div className="rounded-2xl bg-[#F9F7F4] border border-black/[0.06] p-4 space-y-2">
              {block.legs.map((leg, i) => (
                <div key={leg.trip_id || i} className="flex items-center justify-between text-[12px]">
                  <span className="text-[#515E7B]">Leg {i + 1}</span>
                  <span className="font-semibold text-[#0A1128]">${(leg.leg_block_value_cents / 100).toFixed(2)}</span>
                </div>
              ))}
              <div className="pt-2 mt-2 border-t border-black/[0.06] flex items-center justify-between text-[12px]">
                <span className="text-[#0A1128] font-semibold">Total block value</span>
                <span className="font-serif text-[18px] text-[#0A1128]">${dollars}</span>
              </div>
            </div>
          </div>
        )}

        {block.status === "available" && (
          <motion.button
            type="button"
            onClick={onClaim}
            disabled={claiming}
            className="w-full mt-6 h-14 rounded-2xl bg-[#ed6500] text-white font-semibold text-[14px] tracking-[0.12em] uppercase shadow-[0_10px_24px_-12px_rgba(237,101,0,0.7)] inline-flex items-center justify-center gap-2 link-pressable disabled:opacity-60"
            whileTap={{ scale: 0.98 }}
            data-testid="block-claim-button"
          >
            {claiming ? <><Loader2 size={16} className="animate-spin"/>Claiming…</> : <><Check size={16}/>Claim block · ${dollars}</>}
          </motion.button>
        )}
      </motion.div>
    </>
  );
}

function Tile({ label, v }) {
  return (
    <div className="rounded-2xl bg-[#F9F7F4] border border-black/[0.06] px-3 py-3">
      <div className="text-[9px] tracking-[0.22em] uppercase font-semibold text-[#515E7B]">{label}</div>
      <div className="font-serif text-[16px] text-[#0A1128] mt-1 leading-tight">{v}</div>
    </div>
  );
}
